<template>
  <div>
    <DataTable
    :loading="contractsLoading"
    :items.sync="contractsWithFormattedDates"
    :headers="headers"
    :customCells="customColumns"
    :actions="tableActions"
    @refresh="$emit('refresh-contracts')"
    @create="$emit('contract-action', { actionType: 'create-contract' })"
    @click:row="onRowClick"
    @filtered-items="onVisibleItemsUpdate"
    resize
    :tableHeightCalculationData="tableHeightCalculationData"
    :containerClass="dataTableContractClass"
    :htmlIdentifier="getHtmlIdentifier"
    showPointerCursor>
      <template #filters v-if="displayFilterChipsBelowSearch">
        <ContractFilterChips
          :filterChips="filterChips"
          @remove-filter-item="removeFilterItem"
        />
      </template>
      <template #actions="{item}">
        <td @click.stop.prevent class="text-center cursor-default">
          <Icon
          icon="mdi-pencil"
          iconColor="success"
          elementId="contract-edit"
          :tooltipText="$t('edit')"
          @icon-clicked="editContract(item)"
          :disabled="!canEdit(item)"/>
          <Icon
          icon="mdi-delete-forever"
          iconColor="error"
          elementId="contract-delete"
          :tooltipText="$t('delete')"
          @icon-clicked="deleteContract(item)"
          :disabled="!userAssignedClaim(UserClaims.ContractManager)"/>
        </td>
      </template>
      <template #custom-controls>
        <v-col cols="auto" class="pl-6" v-if="displayFilterChipsAppendSearch">
          <ContractFilterChips
            :filterChips="filterChips"
            @remove-filter-item="removeFilterItem"
          />
        </v-col>
        <v-col cols="auto">
          <ContractContextMenu
            :elementId="contractContextMenuId"
            :contractMode="contractMode"
            :showRateReview="isTractDetail"
            @download-csv="downloadCSV"
            @import-tickets="importTickets"
            @rate-review="openRateReviewDialog"
            @base-rate-finder="openBaseRateFinder"
            @rate-sheet-divergence="openRateSheetDivergence"
            @scale-sample-frequencies="openScaleSampleFrequencies"
          />
        </v-col>
        <v-col cols="auto">
          <ContractFilter
            :contractMode="contractMode"
            :propFilter="propFilter"
            :showApprovalsFilter="showApprovalsFilter"
            :showMyTractsFilter="!isTractDetail"
            :showTeamFilter="!isTractDetail"
            @update:propFilter="$emit('update:propFilter', $event)"
          />
        </v-col>
        <v-col cols="auto">
          <ColumnAdjuster
            :propColumnSet.sync="columnSet"
            :dialogColor="contractModeColor"
            @new-columns-selected="newColumnsSelected"
          />
        </v-col>
      </template>
      <template #status="{item}">
        <Icon
        dataTestId="contract-status-icon"
        :icon="getStatusIcon(item).iconName"
        :iconColor="getStatusIcon(item).color"
        :tooltipColor="getStatusIcon(item).color"
        :tooltipText="getStatusIcon(item).msg"/>
        <Icon
        v-show="item.paused"
        dataTestId="contract-pauseicon"
        icon="mdi-pause-circle"
        iconColor="warning"
        :tooltipText="$t('contractOnPause')"/>
        <Icon
        icon="mdi-home"
        dataTestId="contract-external-icon"
        :tooltipColor="item.isExternal ? 'grey' : 'black'"
        :iconColor="item.isExternal ? 'grey' : 'black'"
        :tooltipText="item.isExternal ? $t('external') : $t('internal')"/>
        <Icon
        v-show="contractMode === ContractMode.Logs"
        icon="mdi-axe"
        dataTestId="contract-logger-icon"
        :iconColor="item.requiresLogger ? 'brown darken-4' : 'brown lighten-2'"
        :tooltipColor="item.requiresLogger ? 'brown darken-4' : 'brown lighten-1'"
        :tooltipText="item.requiresLogger ? $t('requiresLogger') : $t('noLoggerRequired')"/>
        <Icon
        dataTestId="contact-product-icon"
        :icon="item.acceptsAnyLoad ? 'mdi-lock-open' : 'mdi-lock'"
        :iconColor="item.acceptsAnyLoad ? 'grey' : 'black'"
        :tooltipColor="item.acceptsAnyLoad ? 'grey' : 'black'"
        :tooltipText="item.acceptsAnyLoad ? $t('acceptsAnyProduct') : $t('restrictedProducts')"
        />
        <Icon
        v-if="item.isDraft"
        icon="mdi-file"
        dataTestId="contract-draft-icon"
        :tooltipColor="getTooltipColorForDraft(item.hasPendingTickets)"
        :iconColor="getTooltipColorForDraft(item.hasPendingTickets)"
        :tooltipText="getTooltipTextForDraft(item.hasPendingTickets)"/>
        <Icon
        v-if="showApprovalStatusWarning(item)"
        icon="mdi-file-document-check-outline"
        iconColor="error"
        :tooltipText="approvalStatusWarningText(item)"
        />
        <Icon
        v-if="item.scheduledRateChangeId"
        dataTestId="contract-scheduled-rate-change-icon"
        icon="invoice-text-update"
        iconColor="info"
        :tooltipText="scheduledRateChangeTooltip(item)"
        />
      </template>
      <template #type="{item}">{{
        contractTypeFromInt(item.type)
      }}</template>
      <template #tract="{item}" v-if="!isTractDetail">
        <Icon
        v-if="item.tractIsCertified"
        icon="mdi-certificate-outline"
        iconColor="black"
        :tooltipText="$t('tractIsCertified')"
        />
        <span>{{item.tract || $t('notApplicable')}}</span>
      </template>
      <template #tractType="{item}" >
        <span>{{item.tractType || $t('notApplicable')}}</span>
      </template>
      <template #account="{item}">
        <AccountName
          :certificationStatus="item.accountCertificationStatus"
          :accountId="item.accountId"
        >
          <span>{{item.account}}</span>
        </AccountName>
      </template>
      <template #from-account="{item}">
        <AccountName
          :certificationStatus="item.fromAccountCertificationStatus"
          :accountId="item.fromAccountId"
        >
          <span>{{item.fromAccount || $t('notApplicable')}}</span>
        </AccountName>
      </template>
      <template #setting="{item}">
        <span>{{item.setting || $t('notApplicable')}}</span>
      </template>
      <template #details="{item}">
        <td @click.stop.prevent class="text-center cursor-default">
          <Icon v-if="!isTractDetail && isLogsContractMode"
          icon="mdi-crosshairs-gps"
          iconColor="black"
          margin="mr-1"
          elementId="contract-tract"
          :tooltipText="$t('viewTract')"
          @icon-clicked="viewTract(item)"/>
          <Icon
          icon="mdi-clipboard-outline"
          iconColor="grey"
          margin="mr-1"
          elementId="contract-ticket"
          :tooltipText="$t('viewTickets')"
          @icon-clicked="jumpToTickets(item)"/>
          <Icon
          icon="mdi-information-outline"
          margin=""
          elementId="contract-detail"
          :tooltipText="$t('details')"
          @icon-clicked="showContractDetail(item)"/>
        </td>
      </template>
    </DataTable>

    <Dialog
    :stateId="dialogId"
    @close="resetDialogs"
    >
      <TractRateReview
        v-if="rateVerificationDialog && isTractDetail && tractId"
        :tractId="tractId"
        :contractIds="visibleContractIds"
        @close="resetDialogs"
        @refresh-contracts="$emit('refresh-contracts')"
      />
      <RateVerificationDialog
        v-else-if="rateVerificationDialog"
        @close="resetDialogs"
        :contractMode="contractMode"
      />
    </Dialog>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { utcToLocalDate, utcToCompanyOffsetDstSensitive } from '@/utils/DateFormatter.js'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import RouterJump from '@/model/RouterJump.js'
import ContractHeaders from '@/headers/Contract'
import { ContractStatusReason, ContractType, ContractStatus, UserClaims, ContractApprovalStatus } from '@/utils/Enumerations.js'
import { saveAs } from 'file-saver'
import { userAssignedClaim } from '@/utils/ClaimUtility.js'
import { ContractMode } from '@/utils/Enumerations'
import { cloneDeep, kebabCase } from 'lodash'

export default {
  name: 'ContractTable',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    AccountName: () => import('@/components/account/AccountName.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    ColumnAdjuster: () => import('@/components/core/ColumnAdjuster.vue'),
    ContractFilter: () => import('@/components/contract/ContractFilter.vue'),
    ContractContextMenu: () => import('@/components/contract/ContractContextMenu.vue'),
    ContractFilterChips: () => import('@/components/contract/ContractFilterChips.vue'),
    RateVerificationDialog: () => import('@/components/settlements/rate-verification/RateVerificationDialog.vue'),
    TractRateReview: () => import('@/components/settlements/rate-verification/TractRateReview.vue'),
    Dialog: () => import('@/components/Dialog.vue')
  },

  props: {
    contractMode: {
      type: Object,
      default: () => ({
        value: 0,
        label: 'logs'
      })
    },
    isTractDetail: {
      type: Boolean,
      default: false
    },
    propFilter: {
      type: Object,
      default: undefined
    },
    contracts: {
      type: Array,
      required: true
    },
    tractId: {
      type: Number,
      default: undefined
    }
  },

  data: () => ({
    columnSet: null,
    UserClaims,
    ContractMode,
    rateVerificationDialog: false,
    dialogId: 'rate-review-dialog',
    visibleContractIds: []
  }),

  watch: {
    contractMode (_) {
      this.initializeHeaders()
    }
  },

  computed: {
    ...mapGetters('contract', { contractsLoading: 'contractsLoading', getContractFilter: 'filter' }),
    ...mapGetters('user', ['userInfo', 'companyInfo']),

    contractModeColor () {
      return ContractMode.forInt(this.contractMode.value).details.color
    },

    filter () {
      return this.propFilter || this.getContractFilter
    },

    headers () {
      return this.columnSet?.getColumns() ?? []
    },

    contractsWithFormattedDates () {
      return this.contracts.map(c => ({
        ...c,
        expirationDate: utcToLocalDate(c.expirationDate, 'MM/DD/YYYY'),
        effectiveDate: utcToLocalDate(c.effectiveDate, 'MM/DD/YYYY')
      }))
    },

    isLogsContractMode () {
      return this.contractMode.value === ContractMode.Logs.value
    },

    tableActions () {
      return [
        {
          actionName: 'refresh',
          icon: 'mdi-refresh',
          text: this.$t('refresh'),
          id: 'refresh'
        },
        (!this.isTractDetail && !this.isLogsContractMode) ? {
          actionName: 'create',
          icon: 'mdi-plus',
          text: this.$t('newContract'),
          disabled: !userAssignedClaim(UserClaims.ContractManager),
          id: 'create'
        } : undefined
      ].filter(a => a !== undefined)
    },

    getHtmlIdentifier () {
      return this.isTractDetail ? 'tract-contracts' : 'contract-grid'
    },

    filterChips () {
      const chips = []

      if (this.filter.includeOpen) {
        chips.push({
          text: this.$t('open'),
          key: 'includeOpen',
          icon: 'mdi-check-circle'
        })
      }

      if (this.filter.includeExpired) {
        chips.push({
          text: this.$t('expired'),
          key: 'includeExpired',
          icon: 'mdi-close-circle'
        })
      }

      if (this.filter.includeOnHold) {
        chips.push({
          text: this.$t('onHold'),
          key: 'includeOnHold',
          icon: 'mdi-minus-circle'
        })
      }

      if (this.filter.includeClosed) {
        chips.push({
          text: this.$t('closed'),
          key: 'includeClosed',
          icon: 'mdi-cancel'
        })
      }

      if (this.filter.limitToInternal) {
        chips.push({
          text: this.$t('internalOnly'),
          key: 'limitToInternal',
          icon: 'mdi-home'
        })
      }

      if (this.filter.limitToDraft) {
        chips.push({
          text: this.$t('draftOnly'),
          key: 'limitToDraft',
          icon: 'mdi-file'
        })
      }

      if (this.filter.limitToPaused) {
        chips.push({
          text: this.$t('pausedOnly'),
          key: 'limitToPaused',
          icon: 'mdi-pause-circle'
        })
      }

      if (this.filter.includeProduction) {
        chips.push({
          text: this.$t('production'),
          key: 'includeProduction',
          icon: 'mdi-alpha-p-circle-outline'
        })
      }

      if (this.filter.includeWoodsSale) {
        chips.push({
          text: this.$t('woodsSale'),
          key: 'includeWoodsSale',
          icon: 'mdi-alpha-w-circle-outline'
        })
      }

      if (this.filter.includeByProduct) {
        chips.push({
          text: this.$t('byproductSale'),
          key: 'includeByProduct',
          icon: 'mdi-alpha-s-circle-outline'
        })
      }

      if (this.filter.includeByproductPurchase) {
        chips.push({
          text: this.$t('byproductPurchase'),
          key: 'includeByproductPurchase',
          icon: 'mdi-alpha-p-circle-outline'
        })
      }

      if (this.filter.includeByproductHauling) {
        chips.push({
          text: this.$t('byproductHauling'),
          key: 'includeByproductHauling',
          icon: 'mdi-alpha-h-circle-outline'
        })
      }

      // if (this.filter.includeLogYardSale) { // TODO: This chip is not relevant yet (this is the only contract type visible in its contract mode)
      //   chips.push({
      //     text: this.$t('logYardSale'),
      //     key: 'includeLogYardSale'
      //   })
      // }

      if (this.filter.approvalStatus === ContractApprovalStatus.PendingReview.value) {
        chips.push({
          text: this.$t('toReview'),
          key: 'approvalStatus',
          icon: 'mdi-file-document-check-outline'
        })
      }

      if (this.filter.approvalStatus === ContractApprovalStatus.Returned.value) {
        chips.push({
          text: this.$t('returned'),
          key: 'approvalStatus',
          icon: 'mdi-file-document-check-outline'
        })
      }

      if (this.filter.myTracts && this.contractMode.value === ContractMode.Logs.value) {
        chips.push({
          text: this.$t('myTracts'),
          key: 'myTracts',
          icon: 'mdi-account-circle'
        })
      }

      if (this.filter.teams?.length > 0 && this.contractMode.value === ContractMode.Logs.value) {
        this.filter.teams.forEach(t => {
          chips.push({
            text: t.name,
            key: 'team',
            id: t.teamId,
            icon: 'mdi-account-group'
          })
        })
      }

      return chips
    },

    customColumns () {
      var columns = [
        {
          slotName: 'account',
          value: 'account'
        },
        {
          slotName: 'from-account',
          value: 'fromAccount'
        },
        {
          slotName: 'setting',
          value: 'setting'
        },
        {
          slotName: 'details',
          value: 'details'
        },
        {
          slotName: 'type',
          value: 'type'
        },
        {
          slotName: 'actions',
          value: 'actions'
        },
        {
          slotName: 'accepts-any-load',
          value: 'acceptsAnyLoad'
        },
        {
          slotName: 'status',
          value: 'status'
        },
        {
          slotName: 'tract',
          value: 'tract'
        },
        {
          slotName: 'tractType',
          value: 'tractType'
        }
      ]

      return columns
    },

    showApprovalsFilter () {
      return this.companyInfo.requireApprovalForContractModifications && userAssignedClaim(UserClaims.ContractReviewer)
    },

    dataTableContractClass () {
      let classString = this.appendContractMode('contract-table')
      if (this.isTractDetail) classString += '-tract-detail'
      return classString
    },

    contractContextMenuId () {
      return this.appendContractMode('contract-context-menu')
    },

    displayFilterChipsAppendSearch () {
      return this.$vuetify.breakpoint.xl && this.filterChips.length < 7
    },

    displayFilterChipsBelowSearch () {
      return !this.displayFilterChipsAppendSearch
    },

    tableHeightCalculationData () {
      return {
        offset: this.$vuetify.breakpoint.xl && this.displayFilterChipsAppendSearch ? 350 : 377
      }
    }
  },

  created () {
    this.initializeHeaders()
    if (!this.showApprovalsFilter && !this.propFilter) {
      this.filter.approvalStatus = undefined
    }
  },

  methods: {
    utcToLocalDate,
    userAssignedClaim,
    ...mapMutations('global', ['setRouterJump']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('contract', { setContractFilter: 'setFilter', removeContractFilter: 'removeFilter' }),

    setFilter (filter) {
      if (this.propFilter) {
        this.$emit('update:propFilter', filter)
      } else {
        this.setContractFilter(filter)
      }
    },

    removeFilter (payload) {
      if (this.propFilter) {
        const filter = cloneDeep(this.filter)
        filter[payload.key] = payload.defaultValue
        this.$emit('update:propFilter', filter)
      } else {
        this.removeContractFilter(payload)
      }
    },

    contractTypeFromInt (type) {
      switch (type) {
        case ContractType.ByproductSale.value:
          return this.$t('sale')
        case ContractType.ByproductPurchase.value:
          return this.$t('purchase')
        case ContractType.ByproductHauling.value:
          return this.$t('hauling')
        default:
          return ContractType.fromInt(type)
      }
    },

    onVisibleItemsUpdate (items) {
      this.visibleContractIds = items.map(i => i.contractId)
    },

    contractStatusFromInt: (type) => ContractStatus.fromInt(type),

    initializeHeaders () {
      let columnSet
      switch (this.contractMode.value) {
        case ContractMode.Logs.value:
          columnSet = ContractHeaders.logsContractHeaders()
          if (this.isTractDetail) {
            const dontShowIfDetail = ['tract', 'tractType', 'tractDefaultLogger']
            columnSet.columns = columnSet.columns.filter(c => !dontShowIfDetail.includes(c.value))
          }
          break
        case ContractMode.Byproducts.value:
          columnSet = ContractHeaders.bpContractHeaders()
          break
        case ContractMode.Transfers.value:
        case ContractMode.LogYardSale.value:
          columnSet = ContractHeaders.transferAndLysContractHeaders()
          break
      }
      columnSet.columns.forEach(column => column.setCellClass(`contract-table-${kebabCase(column.value)} contract-table-cell`))
      this.columnSet = columnSet
    },

    jumpToTickets (contract) {
      localStorage.setItem(LocalStorageKeys.BP_TICKETS_VIEW, false)
      const routerJump = new RouterJump('Contract', 'Tickets', contract)
      this.setRouterJump(routerJump)
      this.$router.push('tickets')
    },

    showContractDetail (contract) {
      const event = { actionType: 'contract-detail', contract: contract }
      this.$emit('contract-action', event)
    },

    viewTract (contract) {
      const event = { actionType: 'view-tract', contract: contract }
      this.$emit('contract-action', event)
    },

    openBaseRateFinder () {
      this.$emit('contract-action', { actionType: 'base-rate-finder' })
    },

    openRateSheetDivergence () {
      this.$emit('contract-action', { actionType: 'rate-sheet-divergence' })
    },

    openScaleSampleFrequencies () {
      this.$emit('contract-action', { actionType: 'scale-sample-frequencies' })
    },

    editContract (contract) {
      const event = { actionType: 'edit-contract', contract: contract }
      this.$emit('contract-action', event)
    },

    importTickets (isUpdate) {
      this.$emit('contract-action', { actionType: 'import-tickets', isUpdate: isUpdate })
    },

    deleteContract (contract) {
      const event = { actionType: 'delete-contract', contract: contract }
      this.$emit('contract-action', event)
    },

    removeFilterItem (filterInfo) {
      const { key: filterKey, id } = filterInfo
      if (filterKey === 'team') {
        const filter = cloneDeep(this.filter)
        const newTeams = filter.teams.filter(t => t.teamId !== id)
        filter.teams = newTeams
        filter.teamIds = newTeams.map(t => t.teamId)
        this.setFilter(filter)
      } else {
        if ((filterKey === 'includeProduction' && !this.filter.includeWoodsSale) ||
        filterKey === 'includeLogYardSale' ||
        (filterKey === 'includeByProduct' && !this.filter.includeByproductPurchase && !this.filter.includeByproductHauling)) {
          return
        }

        let defaultFilterValue = false
        if (filterKey === 'includeWoodsSale' && !this.filter.includeProduction) {
          const filter = cloneDeep(this.filter)
          filter.includeProduction = true
          filter[filterKey] = false
          this.setFilter(filter)
          return
        }

        if (filterKey === 'includeByproductPurchase' && !this.filter.includeByProduct && !this.filter.includeByproductHauling) {
          const filter = cloneDeep(this.filter)
          filter.includeByProduct = true
          filter[filterKey] = false
          this.setFilter(filter)
          return
        }

        if (filterKey === 'includeByproductHauling' && !this.filter.includeByProduct && !this.filter.includeByproductPurchase) {
          const filter = cloneDeep(this.filter)
          filter.includeByProduct = true
          filter[filterKey] = false
          this.setFilter(filter)
          return
        }

        const keys = ['includeOpen', 'includeClosed', 'includeOnHold', 'includeExpired']
        const keyIndex = keys.findIndex(k => k === filterKey)
        if (keyIndex !== -1) {
          keys.splice(keyIndex, 1)
          let isOneEnabled = false
          keys.forEach(k => {
            isOneEnabled = this.filter[k] || isOneEnabled
          })

          if (!isOneEnabled && filterKey === 'includeOpen') {
            return
          }

          if (!isOneEnabled) {
            const filter = cloneDeep(this.filter)
            filter[filterKey] = false
            filter.includeOpen = true
            this.setFilter(filter)
            return
          }
        }

        if (filterKey === 'approvalStatus') {
          const filter = cloneDeep(this.filter)
          filter[filterKey] = undefined
          defaultFilterValue = undefined
          this.setFilter(filter)
        }

        this.removeFilter({ key: filterKey, defaultValue: defaultFilterValue })
      }
    },

    downloadCSV () {
      const csvHeaders = ContractHeaders.csvHeaders(this.$i18n.locale, this.isLogsContractMode)
      let dataString = `${csvHeaders.map(h => h.text).join(',')} \n`
      this.contracts.forEach(contract => {
        for (const value of csvHeaders.map(h => h.value)) {
          switch (value) {
            case ('status'):
              dataString += `"${this.contractStatusFromInt(contract.status)}",`
              break
            case ('isExternal'):
              dataString += `"${contract.isExternal ? this.$t('external') : this.$t('internal')}",`
              break
            case ('requiresLogger'):
              dataString += `"${contract.requiresLogger ? this.$t('required') : this.$t('notRequired')}",`
              break
            case ('acceptsAnyLoad'):
              dataString += `"${contract.acceptsAnyLoad ? this.$t('acceptsAny') : this.$t('restrictedProducts')}",`
              break
            case ('isDraft'):
              dataString += `"${contract.isDraft ? this.$t('draft') : this.$t('notDraft')}",`
              break
            case ('type'):
              dataString += `"${this.contractTypeFromInt(contract.type)}",`
              break
            case ('effectiveDate'):
              dataString += `"${utcToLocalDate(contract.effectiveDate, 'MM/DD/YYYY')}",`
              break
            case ('expirationDate'):
              dataString += `"${utcToLocalDate(contract.expirationDate, 'MM/DD/YYYY')}",`
              break
            default:
              dataString += `"${contract[value].replaceAll('"', '""')}",`
          }
        }

        dataString += '\n'
      })
      const blob = new Blob([dataString], { type: 'text/plain;charset=utf-8' })
      saveAs(blob, `Contracts-${moment().format()}.csv`)
    },

    getStatusIcon (contract) {
      const status = contract.status
      let statusReason = ContractStatusReason.fromInt(contract.statusReason)
      if (statusReason !== 'None') {
        statusReason = `${statusReason}`
      } else {
        statusReason = ''
      }
      switch (status) {
        case 0:
          return {
            iconName: 'mdi-checkbox-marked-circle',
            color: 'success',
            msg: this.$t('open')
          }
        case 1:
          return {
            iconName: 'mdi-cancel',
            color: 'error',
            msg: `${this.$t('closed')}
                  ${statusReason}`
          }
        case 2:
          return {
            iconName: 'mdi-minus-circle',
            color: 'warning',
            msg: `${this.$t('onHold')}
                  ${statusReason}`
          }
        case 3:
          return {
            iconName: 'mdi-close-circle',
            color: 'error',
            msg: `${this.$t('expired')}
                  ${statusReason}`
          }
        default:
          return {
            iconName: 'mdi-alert',
            color: 'error',
            msg: this.$t('exception')
          }
      }
    },

    scheduledRateChangeTooltip (contract) {
      const date = utcToCompanyOffsetDstSensitive(contract.rateChangeEffectiveDate).format('MM/DD/YY')
      const tooltip = contract.scheduledRateChangeRequestedByUserName
        ? this.$t('scheduledRateChangeEffectiveBy', { date, name: contract.scheduledRateChangeRequestedByUserName })
        : this.$t('scheduledRateChangeEffective', { date })
      return contract.scheduledRateChangeNote ? `${tooltip}: ${contract.scheduledRateChangeNote}` : tooltip
    },

    getTooltipColorForDraft (hasPendingTickets) {
      return hasPendingTickets ? 'error' : 'black'
    },

    getTooltipTextForDraft (hasPendingTickets) {
      return hasPendingTickets ? this.$t('hasPendingTickets') : this.$t('draftContract')
    },

    newColumnsSelected (columnSet) {
      this.columnSet = columnSet
    },

    showApprovalStatusWarning (contract) {
      return contract.approvalStatus !== ContractApprovalStatus.Approved.value && contract.approvalStatus !== null
    },

    approvalStatusWarningText (contract) {
      return ContractApprovalStatus.fromInt(contract.approvalStatus)
    },

    canEdit (contract) {
      if (!userAssignedClaim(UserClaims.ContractManager)) return false

      if (!this.companyInfo.requireApprovalForContractModifications) return true

      if (this.approvalStateAllowsModification(contract)) return true

      return this.previousModificationByUser(contract)
    },

    approvalStateAllowsModification (contract) {
      return [
        ContractApprovalStatus.Approved.value,
        ContractApprovalStatus.Returned.value,
        null
      ].includes(contract.approvalStatus)
    },

    previousModificationByUser (contract) {
      return contract.approvalStatus === ContractApprovalStatus.PendingModification.value
        ? contract.approvalRequestedByUserId === this.userInfo.applicationUserId
        : false
    },

    appendContractMode (string) {
      return `${string}-${kebabCase(this.contractMode.label)}`
    },

    onRowClick ({ data }) {
      this.showContractDetail(data)
    },

    openRateReviewDialog () {
      this.rateVerificationDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '60%' })
    },

    resetDialogs () {
      this.rateVerificationDialog = false
      this.closeDialogsAtOrAbove(this.dialogId)
    }
  }
}
</script>
